<template>
  <div class="recycle" :class="{ 'has-bar': hasRows }">
    <header class="page-header">
      <div class="titles">
        <p class="kicker">{{ t('recycle.kicker') }}</p>
        <h1>{{ t('recycle.title') }}</h1>
      </div>
      <router-link to="/deck-builder" class="text-btn">
        {{ t('recycle.back') }}
      </router-link>
    </header>

    <output v-if="tournamentHeld" class="notice warn">
      {{ t('recycle.tournamentBlocked') }}
    </output>
    <output
      v-else-if="notice"
      class="notice"
      :class="notice.kind"
    >
      {{ notice.text }}
    </output>

    <p v-if="!hasRows" class="empty">
      {{ t('recycle.empty') }}
    </p>

    <div v-else class="bulk">
      <div class="bulk-row">
        <button
          type="button"
          class="md-btn-tonal"
          :disabled="tournamentHeld || collection.busy || scopeKeys.size < 1"
          @click="selectAll"
        >
          {{ scopeSelected ? t('recycle.clear') : t('recycle.selectAll') }}
        </button>
        <label class="scope">
          <span>{{ t('recycle.selectScope') }}</span>
          <select
            v-model="selectScope"
            class="scope-select"
            :disabled="tournamentHeld || collection.busy"
          >
            <option value="lower">{{ t('recycle.lowerRarity') }}</option>
            <option value="higher">{{ t('recycle.higherRarity') }}</option>
          </select>
        </label>
      </div>
      <p class="bulk-hint">{{ t('recycle.selectHint') }}</p>
    </div>

    <div v-if="hasRows" ref="listEl" class="groups">
      <template v-for="item in visible" :key="item.key">
        <h2 v-if="item.kind === 'head'" class="group-head">
          <span class="group-name">{{ item.name }}</span>
        </h2>
        <button
          v-else
          type="button"
          class="row"
          :class="{ selected: selected.has(item.key) }"
          :disabled="rowDisabled(item)"
          :aria-pressed="selected.has(item.key)"
          @click="toggle(item)"
        >
          <span class="thumb">
            <CardArt
              :card-id="item.cardId"
              :foil="item.foil"
              :alt="item.name"
              intensity="thumb"
            />
          </span>
          <span class="text">
            <span class="name">{{ item.name }}</span>
            <span class="meta">
              {{ t(`packs.rarity.${item.foil}`) }}
              · {{ t('recycle.rowCoins', { n: item.coins }) }}
            </span>
          </span>
          <span class="check" aria-hidden="true" />
        </button>
      </template>
    </div>

    <div v-if="collection.busy" class="veil">
      <span class="spinner" aria-hidden="true" />
      <p>{{ t('recycle.busy') }}</p>
    </div>

    <div v-if="hasRows" class="bar">
      <p class="bar-summary">
        {{ t('recycle.summary', { n: selectedCount, coins: selectedCoins }) }}
      </p>
      <button
        type="button"
        class="md-btn-filled"
        :disabled="tournamentHeld || selectedCount < 1 || collection.busy"
        @click="confirm"
      >
        {{ collection.busy ? t('recycle.busy') : t('recycle.confirm') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import CardArt from '../components/CardArt.vue'
import {
  CARD_FOIL_IDS_BEST_FIRST,
  RECYCLE_COIN_BY_FOIL,
  RECYCLE_MIN_KEEP,
  type CardFoilId,
  type RecycleLine,
} from '@shared/index'
import { applyRecycledDecks } from '../account/sync'
import { ApiError } from '../api/http'
import { allCatalogCards } from '../game/packs'
import { localizedName } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'
import { useCollectionStore } from '../stores/collection'
import { tournamentDeckLockId } from '../stores/deck'
import { useTournamentStore } from '../stores/tournament'
import { useWalletStore } from '../stores/wallet'

const PAGE_SIZE = 50

type Head = {
  kind: 'head'
  key: string
  cardId: string
  name: string
  spare: number
}

type CopyRow = {
  kind: 'row'
  key: string
  cardId: string
  name: string
  foil: CardFoilId
  coins: number
}

type FlatItem = Head | CopyRow

type SelectScope = 'lower' | 'higher'

const { t, locale } = useI18n()
const collection = useCollectionStore()
const wallet = useWalletStore()
const selected = ref(new Set<string>())
const selectScope = ref<SelectScope>('lower')
const notice = ref<{ kind: 'ok' | 'warn'; text: string } | null>(null)
const shown = ref(PAGE_SIZE)
const listEl = ref<HTMLElement | null>(null)

const catalog = new Map(allCatalogCards().map((card) => [String(card.id), card]))

const flat = computed((): FlatItem[] => {
  void locale.value
  const next: FlatItem[] = []
  const named: { cardId: string; name: string; foils: FlatItem[] }[] = []
  for (const [cardId, foils] of Object.entries(collection.copies)) {
    const total = collection.ownedCount(cardId)
    if (total <= RECYCLE_MIN_KEEP) continue
    const card = catalog.get(cardId)
    if (!card) continue
    const rows: CopyRow[] = []
    for (const foil of CARD_FOIL_IDS_BEST_FIRST) {
      const count = foils[foil] ?? 0
      for (let i = 0; i < count; i++) {
        rows.push({
          kind: 'row',
          key: `${cardId}:${foil}:${i}`,
          cardId,
          name: '',
          foil,
          coins: RECYCLE_COIN_BY_FOIL[foil],
        })
      }
    }
    if (rows.length === 0) continue
    named.push({ cardId, name: localizedName(card.id, card.name), foils: rows })
  }
  named.sort((a, b) => a.name.localeCompare(b.name))
  for (const group of named) {
    next.push({
      kind: 'head',
      key: `head:${group.cardId}`,
      cardId: group.cardId,
      name: group.name,
      spare: group.foils.length - RECYCLE_MIN_KEEP,
    })
    for (const row of group.foils) {
      if (row.kind === 'row') row.name = group.name
      next.push(row)
    }
  }
  return next
})

const spareByCard = computed(() => {
  const map = new Map<string, number>()
  for (const item of flat.value) {
    if (item.kind === 'head') map.set(item.cardId, item.spare)
  }
  return map
})

const pickedByCard = computed(() => {
  const map = new Map<string, number>()
  for (const key of selected.value) {
    const cardId = key.slice(0, key.indexOf(':'))
    map.set(cardId, (map.get(cardId) ?? 0) + 1)
  }
  return map
})

const copyCount = computed(() =>
  flat.value.reduce((n, item) => n + (item.kind === 'row' ? 1 : 0), 0),
)

const hasRows = computed(() => copyCount.value > 0)

const visible = computed(() => {
  const limit = shown.value
  const items = flat.value
  let rows = 0
  let end = 0
  while (end < items.length && rows < limit) {
    if (items[end]?.kind === 'row') rows += 1
    end += 1
  }
  return items.slice(0, end)
})

const hiddenCount = computed(() => Math.max(0, copyCount.value - shown.value))

function maybeLoadMore(): void {
  const el = listEl.value
  if (!el || collection.busy || hiddenCount.value < 1 || el.offsetHeight < 200) return
  const bottom = el.getBoundingClientRect().bottom
  if (bottom > window.innerHeight + 480) return
  shown.value += PAGE_SIZE
}

const tournamentHeld = computed(() => tournamentDeckLockId.value != null)

onMounted(() => {
  void useTournamentStore().refresh()
  window.addEventListener('scroll', maybeLoadMore, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', maybeLoadMore)
})

const selectedCount = computed(() => selected.value.size)

const selectedCoins = computed(() => {
  let coins = 0
  for (const key of selected.value) {
    const foil = key.split(':')[1] as CardFoilId
    coins += RECYCLE_COIN_BY_FOIL[foil] ?? 0
  }
  return coins
})

function leftOf(cardId: string): number {
  return Math.max(0, (spareByCard.value.get(cardId) ?? 0) - (pickedByCard.value.get(cardId) ?? 0))
}

/**
 * Best prints are listed first. Fills the spare slots from that end.
 * Copies of the same print stay put, so both options match when a card
 * has only one rarity.
 */
function keysForScope(scope: SelectScope): Set<string> {
  const next = new Set<string>()
  let rows: CopyRow[] = []
  let spare = 0
  const flush = () => {
    let left = spare
    if (left < 1 || rows.length < 1) {
      rows = []
      spare = 0
      return
    }
    const groups: CopyRow[][] = []
    for (const row of rows) {
      const last = groups[groups.length - 1]
      if (last && last[0]?.foil === row.foil) last.push(row)
      else groups.push([row])
    }
    const order = scope === 'higher' ? groups : [...groups].reverse()
    for (const group of order) {
      if (left < 1) break
      const take = Math.min(left, group.length)
      for (const row of group.slice(group.length - take)) next.add(row.key)
      left -= take
    }
    rows = []
    spare = 0
  }
  for (const item of flat.value) {
    if (item.kind === 'head') {
      flush()
      spare = item.spare
    } else {
      rows.push(item)
    }
  }
  flush()
  return next
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const key of a) {
    if (!b.has(key)) return false
  }
  return true
}

const scopeKeys = computed(() => keysForScope(selectScope.value))

const scopeSelected = computed(
  () => scopeKeys.value.size > 0 && setsEqual(selected.value, scopeKeys.value),
)

function selectAll(): void {
  if (tournamentHeld.value || collection.busy || scopeKeys.value.size < 1) return
  notice.value = null
  selected.value = scopeSelected.value ? new Set() : new Set(scopeKeys.value)
}

watch(selectScope, (scope, prev) => {
  if (!prev || collection.busy || tournamentHeld.value) return
  if (!setsEqual(selected.value, keysForScope(prev))) return
  const next = keysForScope(scope)
  if (next.size < 1) {
    selected.value = new Set()
    return
  }
  selected.value = next
  notice.value = null
})

function rowDisabled(row: CopyRow): boolean {
  if (tournamentHeld.value || collection.busy) return true
  if (selected.value.has(row.key)) return false
  return leftOf(row.cardId) < 1
}

function toggle(row: CopyRow): void {
  if (rowDisabled(row)) return
  const next = new Set(selected.value)
  if (next.has(row.key)) next.delete(row.key)
  else next.add(row.key)
  selected.value = next
  notice.value = null
}

watch(tournamentDeckLockId, (id) => {
  if (!id) return
  selected.value = new Set()
})

async function confirm(): Promise<void> {
  if (tournamentHeld.value || selectedCount.value < 1 || collection.busy) return
  const lines = new Map<string, RecycleLine>()
  for (const key of selected.value) {
    const [cardId, foil] = key.split(':')
    if (!cardId || !foil) continue
    const id = `${cardId}:${foil}`
    const prev = lines.get(id)
    if (prev) prev.qty += 1
    else lines.set(id, { cardId, foil: foil as CardFoilId, qty: 1 })
  }
  try {
    const payload = await collection.recycle([...lines.values()])
    if (!payload) {
      notice.value = { kind: 'warn', text: t('recycle.failed') }
      return
    }
    wallet.setCoins(payload.coins)
    applyRecycledDecks(payload)
    selected.value = new Set()
    notice.value = { kind: 'ok', text: t('recycle.done', { n: payload.delta }) }
  } catch (err) {
    notice.value = {
      kind: 'warn',
      text:
        err instanceof ApiError && err.code === 'deck_locked'
          ? t('recycle.tournamentBlocked')
          : t('recycle.failed'),
    }
  }
}
</script>

<style scoped>
.recycle {
  position: relative;
  width: min(720px, 100%);
  margin: 0 auto;
  padding: 24px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.recycle.has-bar {
  padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.titles {
  min-width: 0;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.text-btn {
  flex-shrink: 0;
  min-height: 44px;
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 44px;
  text-decoration: none;
  white-space: nowrap;
}

.notice,
.empty {
  display: block;
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 20px;
}

.notice.ok {
  color: var(--md-sys-color-primary);
}

.notice.warn,
.empty {
  color: var(--md-sys-color-on-surface-variant);
}

.notice.warn {
  color: var(--md-sys-color-error);
}

.bulk {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.bulk-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.scope {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.scope-select {
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: 8px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
}

.scope-select:focus {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.scope-select:disabled {
  opacity: 0.5;
}

.bulk-hint {
  margin: 0;
  max-width: 42rem;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  line-height: 18px;
}

.md-btn-tonal {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
}

.md-btn-tonal:disabled {
  cursor: default;
  opacity: 0.5;
}

.groups {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  padding: 10px 4px 8px;
  background: var(--md-sys-color-surface);
}

.group-name {
  min-width: 0;
  overflow: hidden;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 8px 12px;
  border: 0;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.row.selected {
  box-shadow: inset 0 0 0 2px var(--md-sys-color-primary);
}

.row:disabled {
  cursor: default;
  opacity: 0.45;
}

.thumb {
  width: 48px;
  flex-shrink: 0;
}

.text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.name {
  overflow: hidden;
  font-size: 15px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  line-height: 18px;
}

.check {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border: 2px solid var(--md-sys-color-outline);
  border-radius: 6px;
}

.row.selected .check {
  border-color: var(--md-sys-color-primary);
  background: var(--md-sys-color-primary);
  box-shadow: inset 0 0 0 3px var(--md-sys-color-surface-container);
}

.bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-2);
}

.bar-summary {
  margin: 0;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
}

.md-btn-filled {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
}

.md-btn-filled:disabled {
  cursor: default;
  opacity: 0.5;
}

.veil {
  position: fixed;
  inset: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--md-scrim);
  color: var(--md-sys-color-on-surface);
}

.veil p {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid color-mix(in srgb, var(--md-sys-color-outline) 45%, transparent);
  border-top-color: var(--md-sys-color-primary);
  border-radius: 50%;
  animation: recycle-spin 0.7s linear infinite;
}

@keyframes recycle-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
    opacity: 0.55;
  }
}

@media (min-width: 721px) {
  .bar {
    padding-right: max(272px, calc(256px + env(safe-area-inset-right, 0px)));
  }
}

@media (min-width: 960px) {
  .recycle {
    width: min(1680px, 100%);
    padding-left: 24px;
    padding-right: 24px;
  }

  .groups {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    align-items: start;
  }

  .group-head {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
  }

  h1 {
    font-size: 28px;
    line-height: 34px;
  }

  .bar {
    flex-wrap: wrap;
  }

  .bulk-row,
  .scope {
    align-items: stretch;
    flex-direction: column;
  }

  .scope-select,
  .md-btn-tonal,
  .md-btn-filled {
    width: 100%;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .recycle {
    padding-top: 12px;
  }

  .page-header {
    margin-bottom: 8px;
  }

  h1 {
    font-size: 22px;
    line-height: 28px;
  }

  .bar {
    gap: 8px;
    padding-top: 8px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  }

  .recycle.has-bar {
    padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  }
}
</style>
